'use client';
import { ProductCardContext } from '.';
import { ComponentProps, useContext } from 'react';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { ProductCardProps } from './types';

type Props = ComponentProps<typeof Layout> &
    Partial<Pick<ProductCardProps, 'imageUrl' | 'name'>> & {
        localizedName?: string;
        imageProps?: ComponentProps<typeof Media>;
    };
export const ProductCardImage = ({
    imageUrl,
    name,
    localizedName: localizedNameProp,
    media,
    children,
    imageProps,
    ...props
}: Props) => {
    const context = useContext(ProductCardContext);
    const getLocalizedValue = useGetLocalizedValue();

    const finalImageUrl = imageUrl ?? context.imageUrl;
    const finalName = name ?? context.name;
    const localizedName = localizedNameProp ?? (finalName ? getLocalizedValue(finalName) : '');

    if (!finalImageUrl) {
        return null;
    }

    const initial = media?.initial;
    // Prefab overrides often set aspectRatio/height to "unset"/"auto" (intrinsic sizing).
    // Absolute fill needs a real box — those values count as unsized.
    const isEffectiveBoxSize = (value: unknown) =>
        value != null && value !== 'auto' && value !== 'unset' && value !== 'initial';
    const hasBoxSize =
        isEffectiveBoxSize(initial?.aspectRatio) ||
        isEffectiveBoxSize(initial?.height) ||
        isEffectiveBoxSize(initial?.minHeight);

    return (
        <Layout
            data-testid="product-card-image"
            media={{
                ...media,
                initial: {
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    ...initial,
                    // After prefab spread so we override aspect-ratio: unset / height: auto.
                    ...(!hasBoxSize ? { aspectRatio: '1 / 1' } : {}),
                    '& > img, & > video': {
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                    },
                },
            }}
            {...props}
        >
            {finalImageUrl && (
                <Media
                    data={{
                        url: finalImageUrl,
                        alt: `product-${localizedName}-image`,
                    }}
                    sizes="19.5rem"
                    data-testid="product-card-image"
                    {...imageProps}
                />
            )}
            {children}
        </Layout>
    );
};
